import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';

const StudentProfile = () => {
  const { user } = useAuth();
  const [profileData, setProfileData] = useState({
    name: user?.name || 'John Doe',
    email: user?.email || 'student@example.com',
    roll: 'STU001',
    class: 'Grade 10',
    section: 'A',
    session: '2023-2024',
    dateOfBirth: '2008-05-15',
    bloodGroup: 'O+',
    guardianName: 'Robert Doe',
    guardianPhone: '+1234567891',
    phone: '+1234567890',
    address: '123 Education Street, City, Country'
  });

  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState(profileData);

  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        const response = await api.get(`/students/user/${user?._id}`);
        const studentData = response.data.data;
        
        const profileInfo = {
          name: studentData.userId?.name || user?.name || 'John Doe',
          email: studentData.userId?.email || user?.email || 'student@example.com',
          roll: studentData.rollNumber || 'STU001',
          class: studentData.class || 'Grade 10',
          section: studentData.section || 'A',
          session: studentData.session || '2023-2024',
          dateOfBirth: studentData.dateOfBirth || '2008-05-15',
          bloodGroup: studentData.bloodGroup || 'O+',
          guardianName: studentData.guardianName || 'Robert Doe',
          guardianPhone: studentData.guardianPhone || '+1234567891',
          phone: studentData.phone || '+1234567890',
          address: studentData.address || '123 Education Street, City, Country'
        };
        
        setProfileData(profileInfo);
        setFormData(profileInfo);
      } catch (error) {
        console.error('Error fetching profile data:', error);
        // Fallback to default data if API fails
        setFormData(profileData);
      }
    };

    if (user) {
      fetchProfileData();
    }
  }, [user]);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleEditToggle = async () => {
    if (editMode) {
      // Save changes to backend
      try {
        await api.put(`/students/user/${user?._id}`, {
          ...formData,
          userId: user._id
        });
        setProfileData(formData);
        alert('Profile updated successfully!');
      } catch (error) {
        console.error('Error updating profile:', error);
        alert('Failed to update profile. Please try again.');
      }
    }
    setEditMode(!editMode);
  };

  const handleCancel = () => {
    setFormData(profileData);
    setEditMode(false);
  };

  return (
    <div className="min-h-screen py-8">
      <div className="container mx-auto px-4">
        <h1 className="text-3xl font-bold text-gray-800 mb-8">My Profile</h1>
        
        <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-md p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold text-gray-800">Student Information</h2>
            <button
              onClick={handleEditToggle}
              className="bg-primary text-white px-4 py-2 rounded hover:bg-blue-700 transition"
            >
              {editMode ? 'Save Changes' : 'Edit Profile'}
            </button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-gray-700 mb-2">Full Name</label>
              {editMode ? (
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full p-2 border rounded"
                />
              ) : (
                <p className="text-gray-800">{profileData.name}</p>
              )}
            </div>
            
            <div>
              <label className="block text-gray-700 mb-2">Email</label>
              {editMode ? (
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full p-2 border rounded"
                />
              ) : (
                <p className="text-gray-800">{profileData.email}</p>
              )}
            </div>
            
            <div>
              <label className="block text-gray-700 mb-2">Roll Number</label>
              <p className="text-gray-800">{profileData.roll}</p>
            </div>
            
            <div>
              <label className="block text-gray-700 mb-2">Class</label>
              {editMode ? (
                <select
                  name="class"
                  value={formData.class}
                  onChange={handleInputChange}
                  className="w-full p-2 border rounded"
                >
                  <option value="Nursery">Nursery</option>
                  <option value="KG">KG</option>
                  <option value="Grade 1">Grade 1</option>
                  <option value="Grade 2">Grade 2</option>
                  <option value="Grade 3">Grade 3</option>
                  <option value="Grade 4">Grade 4</option>
                  <option value="Grade 5">Grade 5</option>
                  <option value="Grade 6">Grade 6</option>
                  <option value="Grade 7">Grade 7</option>
                  <option value="Grade 8">Grade 8</option>
                  <option value="Grade 9">Grade 9</option>
                  <option value="Grade 10">Grade 10</option>
                  <option value="Grade 11">Grade 11</option>
                  <option value="Grade 12">Grade 12</option>
                </select>
              ) : (
                <p className="text-gray-800">{profileData.class}</p>
              )}
            </div>
            
            <div>
              <label className="block text-gray-700 mb-2">Section</label>
              {editMode ? (
                <select
                  name="section"
                  value={formData.section}
                  onChange={handleInputChange}
                  className="w-full p-2 border rounded"
                >
                  <option value="A">A</option>
                  <option value="B">B</option>
                  <option value="C">C</option>
                  <option value="D">D</option>
                </select>
              ) : (
                <p className="text-gray-800">{profileData.section}</p>
              )}
            </div>
            
            <div>
              <label className="block text-gray-700 mb-2">Session</label>
              {editMode ? (
                <input
                  type="text"
                  name="session"
                  value={formData.session}
                  onChange={handleInputChange}
                  className="w-full p-2 border rounded"
                  placeholder="e.g. 2023-2024"
                />
              ) : (
                <p className="text-gray-800">{profileData.session}</p>
              )}
            </div>
            
            <div>
              <label className="block text-gray-700 mb-2">Date of Birth</label>
              {editMode ? (
                <input
                  type="date"
                  name="dateOfBirth"
                  value={formData.dateOfBirth}
                  onChange={handleInputChange}
                  className="w-full p-2 border rounded"
                />
              ) : (
                <p className="text-gray-800">{profileData.dateOfBirth}</p>
              )}
            </div>
            
            <div>
              <label className="block text-gray-700 mb-2">Blood Group</label>
              {editMode ? (
                <input
                  type="text"
                  name="bloodGroup"
                  value={formData.bloodGroup}
                  onChange={handleInputChange}
                  className="w-full p-2 border rounded"
                  placeholder="e.g. O+, AB-"
                />
              ) : (
                <p className="text-gray-800">{profileData.bloodGroup}</p>
              )}
            </div>
            
            <div>
              <label className="block text-gray-700 mb-2">Phone</label>
              {editMode ? (
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full p-2 border rounded"
                  placeholder="Enter phone number"
                />
              ) : (
                <p className="text-gray-800">{profileData.phone}</p>
              )}
            </div>
            
            <div>
              <label className="block text-gray-700 mb-2">Guardian Name</label>
              {editMode ? (
                <input
                  type="text"
                  name="guardianName"
                  value={formData.guardianName}
                  onChange={handleInputChange}
                  className="w-full p-2 border rounded"
                  placeholder="Enter guardian's name"
                />
              ) : (
                <p className="text-gray-800">{profileData.guardianName}</p>
              )}
            </div>
            
            <div>
              <label className="block text-gray-700 mb-2">Guardian Phone</label>
              {editMode ? (
                <input
                  type="tel"
                  name="guardianPhone"
                  value={formData.guardianPhone}
                  onChange={handleInputChange}
                  className="w-full p-2 border rounded"
                  placeholder="Enter guardian's phone number"
                />
              ) : (
                <p className="text-gray-800">{profileData.guardianPhone}</p>
              )}
            </div>
          </div>
          
          <div className="mt-6">
            <label className="block text-gray-700 mb-2">Address</label>
            {editMode ? (
              <textarea
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                rows="3"
                className="w-full p-2 border rounded"
                placeholder="Enter address"
              ></textarea>
            ) : (
              <p className="text-gray-800">{profileData.address}</p>
            )}
          </div>
          
          {editMode && (
            <div className="mt-6 flex justify-end space-x-4">
              <button
                onClick={handleCancel}
                className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600 transition"
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StudentProfile;